import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import {
  CheckCircle, Search, Edit2, Trash2, Plus, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import AssetsSidebar from '../components/common/AssetsSidebar/AssetsSidebar';
import './Dashboard.css';
import './Locations.css';
import './styles/pages-common.css';

// ─── Initial locations data ───────────────────────────────────────────────────
// These will be overridden by the API fetch

const EMPTY_FORM = { siteId: '', location: '', timezoneName: '', utcOffset: '' };

// ─── Component ────────────────────────────────────────────────────────────────
export const Locations = ({ setView }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [alert, setAlert] = useState(null);
  const [locations, setLocations] = useState(null);
  const [sitesList, setSitesList] = useState([]);
  const [filterSiteId, setFilterSiteId] = useState('');   // toolbar "Select Site" filter
  const [searchQuery, setSearchQuery] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [activeTopNav, setActiveTopNav] = useState('Asset');

  // ── Form state ───────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null); // null = Add mode
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Fetch user ──────────────────────────────────────────────────────────────
  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return null;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (data.status && data.data?.user) {
        const userData = data.data.user;
        if (userData.profile_picture && !userData.picture) userData.picture = userData.profile_picture;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
      throw new Error('Invalid data');
    } catch {
      const stored = localStorage.getItem('user');
      if (stored) { const u = JSON.parse(stored); setUser(u); return u; }
      return null;
    }
  };

  const fetchSites = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sites/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          if (setView) setView('signin');
          return;
        }
        throw new Error('Failed to fetch sites');
      }
      const data = await response.json();
      if (data && data.status && Array.isArray(data.data)) {
        setSitesList(data.data.map(s => ({
          id: s.id || s.site_id,
          name: s.site_name || '',
        })));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLocations = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          if (setView) setView('signin');
          return;
        }
        throw new Error('Failed to fetch locations');
      }
      const data = await response.json();
      if (data && data.status && Array.isArray(data.data)) {
        setLocations(data.data.map(l => ({
          id: l.id || l.location_id,
          siteId: l.site || l.site_id,
          siteName: l.site_name || l.site__site_name || '',
          name: l.location_name || '',
          timezoneName: l.timezone_name || '',
          utcOffset: l.utc_offset || '',
        })));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchUserDetails().then(userData => {
      if (!userData && isMounted) {
        setAlert({ type: 'error', message: 'Error loading user information' });
        setTimeout(() => setView && setView('signin'), 500);
      }
    });

    // Fetch sites and locations concurrently in the background
    fetchSites();
    fetchLocations();

    return () => {
      isMounted = false;
    };
  }, [setView]);

  // ── Filtered + sorted list ────────────────────────────────────────────────
  const displayed = (locations || [])
    .filter(loc =>
      (!filterSiteId || loc.siteId === Number(filterSiteId)) &&
      (!searchQuery || loc.name.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) =>
      sortDir === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );

  // ── Open Add form ─────────────────────────────────────────────────────────
  const openAddForm = () => {
    setEditingLocation(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setShowForm(true);
  };

  // ── Open Edit form ────────────────────────────────────────────────────────
  const openEditForm = (loc) => {
    setEditingLocation(loc);
    setFormData({
      siteId: String(loc.siteId),
      location: loc.name,
      timezoneName: loc.timezoneName || '',
      utcOffset: loc.utcOffset || '',
    });
    setFormErrors({});
    setShowForm(true);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = () => {
    setLocations(prev => prev.filter(l => l.id !== pendingDelete));
    setAlert({ type: 'success', message: 'Location deleted successfully.' });
    setTimeout(() => setAlert(null), 3000);
    setPendingDelete(null);
  };

  // ── Form field change ─────────────────────────────────────────────────────
  const handleFormChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  // ── Validate & submit ─────────────────────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.siteId) errors.siteId = 'Please select a site.';
    if (!formData.location.trim()) errors.location = 'Location name is required.';
    if (!formData.timezoneName.trim()) errors.timezoneName = 'Timezone is required.';
    if (!formData.utcOffset.trim()) errors.utcOffset = 'UTC offset is required.';
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    const site = sitesList.find(s => s.id === Number(formData.siteId));
    const accessToken = localStorage.getItem('accessToken');

    if (editingLocation) {
      // ── UPDATE existing location ──
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/update/${editingLocation.id}/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site: Number(formData.siteId),
            location_name: formData.location,
            timezone_name: formData.timezoneName,
            utc_offset: formData.utcOffset,
            last_updated_by: user?.username || user?.user_id || user?.id,
          })
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('accessToken');
            if (setView) setView('signin');
            return;
          }
          throw new Error('Failed to update location');
        }

        await fetchLocations();
        setAlert({ type: 'success', message: `Location "${formData.location}" updated successfully!` });
        setTimeout(() => setAlert(null), 3000);
        setFormData(EMPTY_FORM);
        setEditingLocation(null);
        setShowForm(false);
      } catch (error) {
        setAlert({ type: 'error', message: error.message || 'Error updating location' });
      }
    } else {
      // ── ADD new location ──
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/create/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site: Number(formData.siteId),
            location_name: formData.location,
            timezone_name: formData.timezoneName,
            utc_offset: formData.utcOffset,
            created_by: user?.username || user?.user_id || user?.id,
            last_updated_by: user?.username || user?.user_id || user?.id,
          })
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('accessToken');
            if (setView) setView('signin');
            return;
          }
          throw new Error('Failed to create location');
        }

        await fetchLocations();
        setAlert({ type: 'success', message: `Location "${formData.location}" added successfully!` });
        setTimeout(() => setAlert(null), 3000);
        setFormData(EMPTY_FORM);
        setEditingLocation(null);
        setShowForm(false);
      } catch (error) {
        setAlert({ type: 'error', message: error.message || 'Error creating location' });
      }
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    setFormData(EMPTY_FORM);
    setEditingLocation(null);
    setFormErrors({});
    setShowForm(false);
  };

  const isEditMode = Boolean(editingLocation);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="assets-root"
    >

      {/* ══ 1. MAIN HEADER ══════════════════════════════════════════════════════ */}
      <InnerHeader
        user={user}
        setView={setView}
        onProfilePhotoSuccess={async () => {
          setTimeout(async () => { await fetchUserDetails(); }, 500);
          setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
          setTimeout(() => setAlert(null), 3000);
        }}
      />

      {/* ══ 2. SECONDARY BAR ════════════════════════════════════════════════════ */}
      <div className="assets-secondary-bar">
        <div className="assets-title-wrap">
          <div className="assets-plus-icon"><Plus size={13} /></div>
          <span className="assets-page-title">ASSETS</span>
        </div>
        <div className="assets-top-nav">
          {/* Navigation buttons commented out as requested - preserved for future use
          {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
            .filter(label => {
              if (label === 'Home') return true;
              const role = user?.role;
              const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
              if (label === 'Analytics' && normRole !== 'superadmin') return false;
              if (normRole === 'technicalsupport') return true;
              if (normRole === 'superadmin') return true;
              if (normRole === 'government') return label === 'Dashboard';
              if (normRole === 'maintenance' || normRole === 'research') {
                return label === 'Dashboard' || label === 'Maintenance';
              }
              if (normRole === 'assetmanagement') {
                return label !== 'Maintenance' && label !== 'Export Reports';
              }
              return true;
            })
            .map(label => (
              <button
                key={label}
                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                disabled={label === 'Export Reports'}
                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                onClick={() => {
                  setActiveTopNav(label);
                  if (label === 'Home') setView && setView('home');
                  else if (label === 'Dashboard') setView && setView('dashboard');
                  else if (label === 'Analytics') setView && setView('analytics');
                  else if (label === 'Asset') setView && setView('assets-list');
                  else if (label === 'Supplier') setView && setView('supplier-list');
                  else if (label === 'Pole Design') setView && setView('pole-designs');
                  else if (label === 'Maintenance') setView && setView('maintenance');
                }}
              >
                {label}
              </button>
            ))}
          */}
          <button
            className="assets-top-nav-btn"
            onClick={() => setView && setView('home')}
            title="Back to Home"
          >
            ← Home
          </button>
        </div>
      </div>

      {/* ══ 3. BODY ═════════════════════════════════════════════════════════════ */}
      <div className="assets-body">

        {/* Sidebar — Locations is active */}
        <AssetsSidebar setView={setView} activeItem="Locations" />

        {/* Main content */}
        <div className="assets-main">

          {/* Alert */}
          {alert && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`assets-alert ${alert.type}`}
            >
              <CheckCircle size={18} />
              <span className="alert-message-flex">{alert.message}</span>
              <button className="assets-alert-close" onClick={() => setAlert(null)}>×</button>
            </motion.div>
          )}

          {/* ── ADD / EDIT FORM ──────────────────────────────────────────────── */}
          {showForm ? (
            <motion.div
              key="loc-form"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="site-form-card"
            >
              <div className="site-form-header">
                <h2 className="site-form-title">
                  {isEditMode ? 'Edit Location' : 'Add New Locations'}
                </h2>
                <p className="site-form-subtitle">
                  {isEditMode
                    ? 'Update the location details below.'
                    : 'Enter the data about your new location in the fields below and we will add it to your list.'}
                </p>
              </div>

              <form className="site-form" onSubmit={handleFormSubmit} noValidate>

                {/* Select Site dropdown */}
                <div className="site-form-select-wrap">
                  <select
                    className={`site-form-select${formErrors.siteId ? ' error' : ''}`}
                    name="siteId"
                    value={formData.siteId}
                    onChange={handleFormChange}
                  >
                    <option value="" disabled>Select Site *</option>
                    {sitesList.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="site-form-select-icon" />
                </div>
                {formErrors.siteId && <p className="site-form-error">{formErrors.siteId}</p>}

                {/* Location name */}
                <input
                  className={`site-form-input${formErrors.location ? ' error' : ''}`}
                  type="text"
                  name="location"
                  placeholder="Location *"
                  value={formData.location}
                  onChange={handleFormChange}
                />
                {formErrors.location && <p className="site-form-error">{formErrors.location}</p>}

                {/* Timezone and UTC Offset */}
                <div className="site-form-row">
                  <div>
                    <input
                      className={`site-form-input${formErrors.timezoneName ? ' error' : ''}`}
                      type="text"
                      name="timezoneName"
                      placeholder="Timezone *"
                      value={formData.timezoneName}
                      onChange={handleFormChange}
                    />
                    {formErrors.timezoneName && <p className="site-form-error">{formErrors.timezoneName}</p>}
                  </div>
                  <div>
                    <input
                      className={`site-form-input${formErrors.utcOffset ? ' error' : ''}`}
                      type="text"
                      name="utcOffset"
                      placeholder="UTC Offset *"
                      value={formData.utcOffset}
                      onChange={handleFormChange}
                    />
                    {formErrors.utcOffset && <p className="site-form-error">{formErrors.utcOffset}</p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="site-form-actions">
                  <button type="submit" className="site-form-btn-add">
                    {isEditMode ? 'Update' : 'Add'}
                  </button>
                  <button type="button" className="site-form-btn-cancel" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>

              </form>
            </motion.div>

          ) : (

            /* ── LOCATIONS TABLE ────────────────────────────────────────────── */
            <div className="assets-card loc-card">

              {/* Toolbar */}
              <div className="loc-toolbar">
                {/* Select Site filter */}
                <div className="loc-filter-wrap">
                  <select
                    className="loc-filter-select"
                    value={filterSiteId}
                    onChange={e => setFilterSiteId(e.target.value)}
                  >
                    <option value="">Select Site</option>
                    {sitesList.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="loc-filter-icon" />
                </div>

                {/* Search */}
                <div className="assets-search-wrap loc-search">
                  <Search size={14} color="#64748b" />
                  <input
                    className="assets-search-input"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Add button */}
                <button className="loc-add-btn" onClick={openAddForm}>
                  Add New Location
                </button>
              </div>

              {/* Table */}
              <div className="assets-table-wrap">
                <table className="assets-table loc-table">
                  <thead>
                    <tr>
                      <th
                        className="assets-th sortable loc-th-location"
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                      >
                        Location
                        <ChevronsUpDown size={13} className="loc-sort-icon" />
                      </th>
                      <th className="assets-th loc-th-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations === null ? (
                      [...Array(5)].map((_, i) => (
                        <tr key={`skeleton-${i}`}>
                          <td colSpan={2} className="assets-td">
                            <div className="skeleton-loader" style={{ height: '24px', margin: '4px 0' }}>
                              <div className="skeleton-bar" style={{ height: '100%', width: '100%' }}></div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : displayed.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="assets-td assets-table-empty">No locations found.</td>
                      </tr>
                    ) : displayed.map((loc, idx) => (
                      <tr
                        key={loc.id}
                        className={idx % 2 === 0 ? 'assets-table-row-even' : 'assets-table-row-odd'}
                      >
                        <td className="assets-td">{loc.name}</td>
                        <td className="assets-td">
                          <span className="assets-action-group">
                            <button
                              className="assets-action-btn edit"
                              title="Edit"
                              onClick={() => openEditForm(loc)}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              className="assets-action-btn delete"
                              title="Delete"
                              onClick={() => setPendingDelete(loc.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ══ 4. FOOTER ═══════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {pendingDelete && (
          <ConfirmDeleteModal
            message="Are you sure you want to delete this location? This action cannot be undone."
            onConfirm={handleDelete}
            onCancel={() => setPendingDelete(null)}
          />
        )}
      </AnimatePresence>

      <InnerFooter />

    </motion.div>
  );
};

export default Locations;
